// ==================== VIEW GROUP MEMBERS MODAL ====================

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  X,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  XCircle,
  UserMinus,
  UserPlus,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Zap,
  Tag,
} from 'lucide-react';
import { Modal, Button, Badge, Checkbox, Select } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { contactsService } from '../../../services/contacts.service';
import type { GroupMember, GroupSummary, AvailableContact } from '../../../services/contacts.service';
import { getCountryFlag, getCountryName } from '../../../constants/countries.constants';

// ==================== CONSTANTS ====================

const MIN_SEARCH_CHARS = 3;
const DEBOUNCE_DELAY = 500;

// ==================== TYPES ====================

interface ViewMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  onMembersChanged?: () => void;
}

// ==================== FILTER OPTIONS ====================

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All Contacts' },
  { value: 'true', label: 'Opted In Only' },
  { value: 'false', label: 'Opted Out Only' },
];

// ==================== MEMBER CARD COMPONENT ====================

interface MemberCardProps {
  member: GroupMember;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onRemove: () => void;
  isRemoving: boolean;
}

const MemberCard = ({ member, isSelected, onSelect, onRemove, isRemoving }: MemberCardProps) => {
  const { contact } = member;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
    >
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onChange={(e) => onSelect(e.target.checked)}
        size="sm"
      />

      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-medium text-orange-400">
          {contact.name?.charAt(0)?.toUpperCase() || contact.phoneNumber.charAt(0)}
        </span>
      </div>

      {/* Contact Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white truncate">
            {contact.name || 'No Name'}
          </p>
          {contact.optedIn ? (
            <Badge variant="success" size="sm" className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Opted In
            </Badge>
          ) : (
            <Badge variant="error" size="sm" className="flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Opted Out
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-(--color-muted-gray)">
          <span className="flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {contact.phoneNumber}
          </span>
          {contact.email && (
            <span className="flex items-center gap-1 truncate">
              <Mail className="w-3 h-3" />
              {contact.email}
            </span>
          )}
          {contact.country && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {getCountryFlag(contact.country)} {getCountryName(contact.country)}
            </span>
          )}
        </div>
        {/* Tags */}
        {contact.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {contact.tags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white/10 text-(--color-soft-gray)"
              >
                <Tag className="w-2.5 h-2.5" />
                {tag}
              </span>
            ))}
            {contact.tags.length > 3 && (
              <span className="text-xs text-(--color-muted-gray)">
                +{contact.tags.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Remove Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        disabled={isRemoving}
        title="Remove from group"
        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300 hover:bg-red-500/10"
      >
        {isRemoving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <UserMinus className="w-4 h-4" />
        )}
      </Button>
    </motion.div>
  );
};

// ==================== ADD MEMBERS PANEL COMPONENT ====================

interface AddMembersPanelProps {
  groupId: string;
  onClose: () => void;
  onMembersAdded: () => void;
}

const AddMembersPanel = ({ groupId, onClose, onMembersAdded }: AddMembersPanelProps) => {
  // State
  const [contacts, setContacts] = useState<AvailableContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ==================== DEBOUNCED SEARCH ====================

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery) {
      setDebouncedSearch('');
      return;
    }

    if (searchQuery.length < MIN_SEARCH_CHARS) {
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, DEBOUNCE_DELAY);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // ==================== FETCH CONTACTS ====================

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await contactsService.getAvailableContactsForGroup(groupId, {
        search: debouncedSearch || undefined,
        page,
        limit: 10,
      });

      setContacts(response.items);
      setTotal(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [groupId, debouncedSearch, page]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ==================== HANDLERS ====================

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (!value) {
      setDebouncedSearch('');
      setPage(1);
    }
  };

  const handleSelectContact = (contactId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(contactId);
      } else {
        next.delete(contactId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return;

    try {
      setAdding(true);
      setError(null);
      
      const result = await contactsService.addContactsToGroup(groupId, Array.from(selectedIds));
      
      setSuccess(`Added ${result.added} contact(s) to group`);
      setSelectedIds(new Set());
      
      // Refresh list and notify parent
      await fetchContacts();
      onMembersAdded();
      
      // Clear success after delay
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to add contacts:', err);
      setError('Failed to add contacts to group');
    } finally {
      setAdding(false);
    }
  };

  // ==================== COMPUTED ====================

  const allSelected = contacts.length > 0 && selectedIds.size === contacts.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < contacts.length;

  // ==================== RENDER ====================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-(--color-muted-gray)" />
          </button>
          <div>
            <h3 className="text-sm font-medium text-white">Add Members</h3>
            <p className="text-xs text-(--color-muted-gray)">
              Select contacts to add to this group
            </p>
          </div>
        </div>
        {selectedIds.size > 0 && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddSelected}
            disabled={adding}
          >
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <UserPlus className="w-4 h-4 mr-2" />
            )}
            Add {selectedIds.size} Contact{selectedIds.size !== 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Success Message */}
      {success && (
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <p className="text-sm text-emerald-300">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-sm text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4 text-red-400" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray) pointer-events-none" />
          <input
            type="text"
            placeholder="Search contacts (min 3 chars)..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full h-11 px-4 pl-10 text-sm text-white bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
          />
        </div>
        {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_CHARS && (
          <p className="text-xs text-amber-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Type at least {MIN_SEARCH_CHARS} characters to search
          </p>
        )}
        {searchQuery.length >= MIN_SEARCH_CHARS && searchQuery !== debouncedSearch && (
          <p className="text-xs text-(--color-muted-gray) flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Searching...
          </p>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          <p className="text-sm text-(--color-muted-gray) mt-2">Loading contacts...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && contacts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8">
          <Users className="w-10 h-10 text-(--color-muted-gray) mb-3" />
          <p className="text-sm text-white font-medium">No contacts available</p>
          <p className="text-xs text-(--color-muted-gray) mt-1 text-center">
            {searchQuery
              ? 'No contacts match your search criteria'
              : 'All opted-in contacts are already in this group'}
          </p>
        </div>
      )}

      {/* Contacts List */}
      {!loading && contacts.length > 0 && (
        <>
          {/* Select All Header */}
          <div className="flex items-center gap-3 px-3 py-2 bg-white/5 rounded-lg">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              size="sm"
            />
            <span className="text-xs text-(--color-muted-gray)">
              {allSelected ? 'Deselect all' : 'Select all'} ({total} available)
            </span>
          </div>

          {/* Contact List */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {contacts.map((contact) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                  selectedIds.has(contact.id)
                    ? 'bg-orange-500/10 border-orange-500/30'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                onClick={() => handleSelectContact(contact.id, !selectedIds.has(contact.id))}
              >
                <Checkbox
                  checked={selectedIds.has(contact.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectContact(contact.id, e.target.checked);
                  }}
                  size="sm"
                />
                
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center shrink-0">
                  <span className="text-xs font-medium text-orange-400">
                    {contact.name?.charAt(0)?.toUpperCase() || contact.phoneNumber.charAt(0)}
                  </span>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {contact.name || 'No Name'}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-(--color-muted-gray)">
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {contact.phoneNumber}
                    </span>
                    {contact.country && (
                      <span>{getCountryFlag(contact.country)}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <p className="text-xs text-(--color-muted-gray)">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== MAIN MODAL COMPONENT ====================

const ViewMembersModal = ({ isOpen, onClose, groupId, onMembersChanged }: ViewMembersModalProps) => {
  // State
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [group, setGroup] = useState<GroupSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Actions
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [bulkRemoving, setBulkRemoving] = useState(false);
  
  // Add members panel
  const [showAddPanel, setShowAddPanel] = useState(false);

  // ==================== DEBOUNCED SEARCH ====================

  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search is empty, update immediately
    if (!searchQuery) {
      setDebouncedSearch('');
      return;
    }

    // If search has fewer than minimum characters, don't search yet
    if (searchQuery.length < MIN_SEARCH_CHARS) {
      return;
    }

    // Set timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, DEBOUNCE_DELAY);

    // Cleanup on unmount or when searchQuery changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // ==================== FETCH MEMBERS ====================

  const fetchMembers = useCallback(async () => {
    if (!groupId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await contactsService.getGroupMembers(groupId, {
        search: debouncedSearch || undefined,
        optedIn: statusFilter === 'all' ? undefined : statusFilter === 'true',
        page,
        limit: 10,
      });

      setMembers(response.items);
      setGroup(response.group);
      setTotal(response.meta.total);
      setTotalPages(response.meta.totalPages);
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('Failed to load group members');
    } finally {
      setLoading(false);
    }
  }, [groupId, debouncedSearch, statusFilter, page]);

  useEffect(() => {
    if (isOpen && groupId) {
      fetchMembers();
    }
  }, [isOpen, groupId, fetchMembers]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setDebouncedSearch('');
      setStatusFilter('all');
      setPage(1);
      setShowAddPanel(false);
    }
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ==================== HANDLERS ====================

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // If clearing search, reset immediately
    if (!value) {
      setDebouncedSearch('');
      setPage(1);
    }
  };

  const handleStatusFilter = (value: string | string[]) => {
    const filterValue = Array.isArray(value) ? value[0] : value;
    setStatusFilter(filterValue);
    setPage(1);
  };

  const handleSelectMember = (memberId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(memberId);
      } else {
        next.delete(memberId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(members.map((m) => m.contact.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleRemoveMember = async (contactId: string) => {
    try {
      setRemovingIds((prev) => new Set(prev).add(contactId));
      await contactsService.removeContactsFromGroup(groupId, [contactId]);
      
      // Refresh list
      await fetchMembers();
      onMembersChanged?.();
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError('Failed to remove member from group');
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    }
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;

    try {
      setBulkRemoving(true);
      await contactsService.removeContactsFromGroup(groupId, Array.from(selectedIds));
      
      // Clear selection and refresh
      setSelectedIds(new Set());
      await fetchMembers();
      onMembersChanged?.();
    } catch (err) {
      console.error('Failed to remove members:', err);
      setError('Failed to remove selected members');
    } finally {
      setBulkRemoving(false);
    }
  };

  // ==================== COMPUTED ====================

  const allSelected = members.length > 0 && selectedIds.size === members.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < members.length;

  // ==================== RENDER ====================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          {group?.color && (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${group.color}20` }}
            >
              <Users className="w-4 h-4" style={{ color: group.color }} />
            </div>
          )}
          <div>
            <span className="text-white">{group?.name || 'Group'} Members</span>
            {group?.type === 'DYNAMIC' && (
              <span className="ml-2 text-xs text-purple-400">
                <Zap className="w-3 h-3 inline mr-1" />
                Dynamic
              </span>
            )}
          </div>
        </div>
      }
      size="lg"
      contentClassName="px-6 py-4"
      footer={
        !showAddPanel ? (
          <div className="flex items-center justify-between w-full">
            {/* Left side - Selection info */}
            <div className="text-sm text-(--color-muted-gray)">
              {selectedIds.size > 0 ? (
                <span className="text-orange-400">{selectedIds.size} selected</span>
              ) : (
                <span>{total} member{total !== 1 ? 's' : ''} total</span>
              )}
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-3">
              {selectedIds.size > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkRemove}
                  disabled={bulkRemoving}
                >
                  {bulkRemoving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <UserMinus className="w-4 h-4 mr-2" />
                  )}
                  Remove Selected
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddPanel(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Members
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : undefined
      }
    >
      {/* Conditional Content */}
      {showAddPanel ? (
        <AddMembersPanel
          groupId={groupId}
          onClose={() => setShowAddPanel(false)}
          onMembersAdded={() => {
            fetchMembers();
            onMembersChanged?.();
          }}
        />
      ) : (
        <div className="space-y-4">
        {/* Search and Filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-3 relative z-10">
            <div className="flex-1 min-w-0 relative z-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--color-muted-gray) pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Search by name, phone, or email (min 3 chars)..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full h-11 px-4 pl-10 text-sm text-white bg-(--color-charcoal-black) border border-(--color-border-gray) rounded-lg placeholder:text-(--color-muted-gray) focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all"
              />
            </div>
            <div className="relative z-20 shrink-0 w-44">
              <Select
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={handleStatusFilter}
                fullWidth
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchMembers}
              disabled={loading}
              className="shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {/* Search hint */}
          {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_CHARS && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Type at least {MIN_SEARCH_CHARS} characters to search
            </p>
          )}
          {searchQuery.length >= MIN_SEARCH_CHARS && searchQuery !== debouncedSearch && (
            <p className="text-xs text-(--color-muted-gray) flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Searching...
            </p>
          )}
        </div>

        {/* Dynamic Group Notice */}
        {group?.type === 'DYNAMIC' && (
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-purple-400 mt-0.5" />
              <div>
                <p className="text-sm text-purple-300 font-medium">Dynamic Group</p>
                <p className="text-xs text-(--color-muted-gray) mt-1">
                  Members are automatically added based on rules. Removing a member manually will exclude them until they match the rules again.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-sm text-(--color-muted-gray) mt-3">Loading members...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && members.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-(--color-muted-gray)" />
            </div>
            <p className="text-white font-medium mb-1">No members found</p>
            <p className="text-sm text-(--color-muted-gray) text-center max-w-sm">
              {searchQuery || statusFilter !== 'all'
                ? 'No members match your current filters. Try adjusting your search.'
                : 'This group has no members yet. Add contacts to this group from the Contacts page.'}
            </p>
          </div>
        )}

        {/* Members List */}
        {!loading && members.length > 0 && (
          <>
            {/* Select All Header */}
            <div className="flex items-center gap-4 px-4 py-2 border-b border-white/10">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                size="sm"
              />
              <span className="text-sm text-(--color-muted-gray)">
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
            </div>

            {/* Members */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              <AnimatePresence mode="popLayout">
                {members.map((member) => (
                  <MemberCard
                    key={member.id}
                    member={member}
                    isSelected={selectedIds.has(member.contact.id)}
                    onSelect={(checked) => handleSelectMember(member.contact.id, checked)}
                    onRemove={() => handleRemoveMember(member.contact.id)}
                    isRemoving={removingIds.has(member.contact.id)}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <p className="text-sm text-(--color-muted-gray)">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      )}
    </Modal>
  );
};

export default ViewMembersModal;

